<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import NavList, { type NavGroup } from '@/components/NavList.vue'
import PageHeader from '@/components/PageHeader.vue'
import { useBackButton } from '@/composables/useBackButton'
import { goBack } from '@/router/goBack'
import { ApiError, getAdminBot, type AdminBot } from '@/api'
import { formatEpoch, languageOptions, prettyJson, rightsList } from '@/admin/bot'
import CommandLink from '@/components/CommandLink.vue'
import UiGroup from '@/components/UiGroup.vue'
import UiList from '@/components/UiList.vue'

// What Telegram reports about the bot, as the last profile sync stored it (backend `bot/profile_sync.py`). Made for a phone: stacked
// label/value pairs in cards instead of a table, long values wrap, the raw answers are folded away.
const { t, locale } = useI18n()
const router = useRouter()
useBackButton(() => goBack(router, '/admin'))
const data = ref<AdminBot | null>(null)
const loading = ref(true)
const loadError = ref('')

// The names, descriptions and commands have a subpage of their own (`AdminBotProfilesPage.vue`).
const profileLinks = computed<NavGroup[]>(() => [
  {
    key: 'profiles',
    items: [
      {
        to: '/admin/bot/profiles',
        icon: '🌐',
        title: t('admin.bot.profiles'),
        hint: t('admin.bot.profilesHint', {
          n: languageOptions(data.value?.profiles ?? []).length,
        }),
      },
    ],
  },
])

function formatDate(value: string | null): string {
  if (!value) return '—'
  try {
    return new Date(value).toLocaleString(locale.value)
  } catch {
    return value
  }
}

function text(value: string | number | null | undefined): string {
  return value === null || value === undefined || value === '' ? '—' : String(value)
}

function yesNo(value: boolean | null): string {
  return value === null ? '—' : value ? t('admin.yes') : t('admin.no')
}

async function load() {
  loading.value = true
  loadError.value = ''
  try {
    data.value = await getAdminBot()
  } catch (e) {
    loadError.value = e instanceof ApiError ? e.message : t('admin.bot.loadFailed')
  } finally {
    loading.value = false
  }
}

onMounted(load)
</script>

<template>
  <div class="bot-page" data-testid="admin-bot">
    <PageHeader
      :title="t('admin.bot.title')"
      :hint="t('admin.bot.hint')"
      back-to="/admin"
      :back-label="t('admin.back')"
    />
    <i18n-t keypath="admin.bot.refreshHint" tag="p" class="ui-hint">
      <template #command><CommandLink command="sync_profile" /></template>
    </i18n-t>

    <p v-if="loading">{{ t('common.loading') }}</p>
    <template v-else-if="loadError">
      <p class="ui-error">{{ loadError }}</p>
      <button type="button" class="ui-btn" @click="load">{{ t('common.retry') }}</button>
    </template>
    <p v-else-if="!data?.info" class="ui-hint" data-testid="bot-empty">
      {{ t('admin.bot.empty') }}
    </p>

    <template v-else>
      <UiGroup :title="t('admin.bot.webhook')">
        <UiList as="dl" class="card" data-testid="bot-webhook">
          <div class="field">
            <dt>{{ t('admin.bot.username') }}</dt>
            <dd>
              {{ data.info.username ? `@${data.info.username}` : '—' }}
              <span class="ui-hint">({{ data.info.bot_id }})</span>
            </dd>
          </div>
          <div class="field">
            <dt>{{ t('admin.bot.url') }}</dt>
            <dd>{{ text(data.info.url) }}</dd>
          </div>
          <div class="field">
            <dt>{{ t('admin.bot.pending') }}</dt>
            <dd>{{ text(data.info.pending_update_count) }}</dd>
          </div>
          <div class="field">
            <dt>{{ t('admin.bot.lastError') }}</dt>
            <dd>
              {{ text(data.info.last_error_message) }}
              <span v-if="data.info.last_error_date" class="ui-hint"
                >({{ formatEpoch(data.info.last_error_date, locale) }})</span
              >
            </dd>
          </div>
          <div class="field">
            <dt>{{ t('admin.bot.lastSyncError') }}</dt>
            <dd>{{ formatEpoch(data.info.last_synchronization_error_date, locale) }}</dd>
          </div>
          <div class="field">
            <dt>{{ t('admin.bot.maxConnections') }}</dt>
            <dd>{{ text(data.info.max_connections) }}</dd>
          </div>
          <div class="field">
            <dt>{{ t('admin.bot.ipAddress') }}</dt>
            <dd>{{ text(data.info.ip_address) }}</dd>
          </div>
          <div class="field">
            <dt>{{ t('admin.bot.certificate') }}</dt>
            <dd>{{ yesNo(data.info.has_custom_certificate) }}</dd>
          </div>
          <div class="field">
            <dt>{{ t('admin.bot.allowedUpdates') }}</dt>
            <dd>
              <span v-for="update in data.info.allowed_updates ?? []" :key="update" class="chip">{{
                update
              }}</span>
              <template v-if="!data.info.allowed_updates?.length">—</template>
            </dd>
          </div>
          <div class="field">
            <dt>{{ t('admin.bot.fetched') }}</dt>
            <dd>{{ formatDate(data.info.fetched_at) }}</dd>
          </div>
          <details class="raw">
            <summary>{{ t('admin.bot.raw') }}</summary>
            <pre>{{ prettyJson(data.info.raw_get_webhook_info) }}</pre>
          </details>
        </UiList>
      </UiGroup>

      <UiGroup :title="t('admin.bot.photo')">
        <UiList as="dl" class="card" data-testid="bot-photo">
          <div class="field">
            <dt>{{ t('admin.bot.photoHash') }}</dt>
            <dd class="mono">{{ text(data.info.photo_sha256) }}</dd>
          </div>
          <div class="field">
            <dt>{{ t('admin.bot.photoSetAt') }}</dt>
            <dd>{{ formatDate(data.info.photo_set_at) }}</dd>
          </div>
        </UiList>
      </UiGroup>

      <NavList :groups="profileLinks" />

      <UiGroup :title="t('admin.bot.rights')">
        <p v-if="data.admin_rights.length === 0" class="ui-hint">{{ t('admin.bot.empty') }}</p>
        <UiList
          as="section"
          v-for="rights in data.admin_rights"
          :key="String(rights.for_channels)"
          class="card"
          data-testid="bot-rights"
        >
          <h4>{{ rights.for_channels ? t('admin.bot.channels') : t('admin.bot.groups') }}</h4>
          <ul class="rights">
            <li
              v-for="entry in rightsList(rights.rights)"
              :key="entry.name"
              class="chip"
              :class="entry.state"
            >
              <span aria-hidden="true">{{
                entry.state === 'yes' ? '✓' : entry.state === 'no' ? '✗' : '?'
              }}</span>
              {{ entry.name }}
              <span class="sr-only">{{ t(`admin.bot.${entry.state}`) }}</span>
            </li>
          </ul>
          <details class="raw">
            <summary>{{ t('admin.bot.raw') }}</summary>
            <pre>{{ prettyJson(rights.raw_get_my_default_administrator_rights) }}</pre>
          </details>
        </UiList>
      </UiGroup>
    </template>
  </div>
</template>

<style scoped>
/* Works down to a 320 px wide screen: nothing here has a fixed width, labels sit above their values and everything long wraps. */
.bot-page {
  min-width: 0;
}

.card {
  padding: 0.25rem 0.85rem 0.6rem;
}

.card + .card {
  margin-top: 0.5rem;
}

dl,
dd,
ul {
  margin: 0;
  padding: 0;
}

.field {
  padding: 0.45rem 0;
}

.field + .field {
  border-top: 1px solid var(--tg-secondary-bg-color);
}

dt {
  font-size: 0.8rem;
  color: var(--tg-hint-color);
}

dd,
.chip,
h4 {
  overflow-wrap: anywhere;
}

.mono {
  font-family: ui-monospace, monospace;
  font-size: 0.8rem;
}

h4 {
  margin: 0.6rem 0 0.2rem;
  font-size: 0.95rem;
}

.rights {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  list-style: none;
}

.chip {
  display: inline-block;
  margin: 0 0.25rem 0.25rem 0;
  padding: 0.1rem 0.5rem;
  border-radius: 0.6rem;
  font-size: 0.78rem;
  background: var(--tg-secondary-bg-color);
  color: var(--tg-hint-color);
}

.rights .chip {
  margin: 0;
}

.chip.yes {
  color: var(--tg-text-color);
}

.raw {
  margin-top: 0.4rem;
}

.raw summary {
  font-size: 0.8rem;
  color: var(--tg-hint-color);
  cursor: pointer;
}

.raw pre {
  max-width: 100%;
  margin: 0.3rem 0 0;
  padding: 0.5rem;
  overflow-x: auto;
  border-radius: 8px;
  font-size: 0.75rem;
  background: var(--tg-secondary-bg-color);
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}
</style>
